import { HtmlWithKatex, renderHtmlWithKatex } from '../helpers/renderKatex';
import { SLOT, lignesTrous } from '../helpers/expressions';
import { membresChaine } from '../../../utils/chaineEgalites';
import PrintEnonce from '../helpers/PrintEnonce';
import { seededShuffle, seedFromPayload } from '../helpers/seededShuffle';
import { consignePour, modeEffectif } from '../helpers/saisiePolicy';
import PrintNumberLine from '../helpers/renderNumberLine';
import PrintConstructionFigure from '../helpers/renderConstructionFigure';
import PrintRepere2D from '../helpers/renderRepere2D';
import { nomCouleur } from '../helpers/couleurs';

/**
 * Renderer print unique pour `payload.type === 'association'`.
 *
 * Dispatche en interne selon `contenu.subType` :
 *   - 'relier'      → 2 colonnes : sources en ordre + targets mélangés (seed)
 *   - 'ordonner'    → liste brute d'items (élève répond sur son cahier)
 *   - 'classer'     → liste mélangée d'items (catégories rappelées via consigne)
 *   - 'texte_trou'  → phrase avec trous soulignés + banque de mots (mode drag)
 *   - 'placer_points_2d' → points à placer dans un repère 2D (coordonnées x;y)
 *
 * Les mélanges sont seedés par `payload.id` ou dérivé (voir `seedFromPayload`)
 * pour que l'aperçu et l'impression affichent exactement le même visuel.
 */

// ─── Relier ────────────────────────────────────────────────────────────

// ciblesLeurres : cibles surnuméraires (sans correspondance) — MÊME construction
// de liste que RelierCorrection (CorrectionsSection) : vraies cibles puis leurres,
// même seed → même ordre sur la feuille et le corrigé.
const RelierBody = ({ pairs, ciblesLeurres, seed, enCahier }) => {
    if (!pairs?.length) return null;
    const sources = pairs.map((p) => p.source);
    const targets = seededShuffle(
        [...pairs.map((p) => p.target), ...(ciblesLeurres || []).map((l) => l.target)],
        seed,
    );
    return (
        <div className="ep-relier-grid">
            <div className="ep-relier-col">
                {sources.map((s, i) => (
                    <div key={`src-${i}`} className="ep-relier-cell">
                        {/* En mode cahier : repères 1/2/3 — l'élève écrit « 1 → C » */}
                        {enCahier && <span className="ep-relier-marque">{i + 1}.</span>}
                        <HtmlWithKatex html={s} />
                    </div>
                ))}
            </div>
            <div className="ep-relier-col">
                {targets.map((t, i) => (
                    <div key={`tgt-${i}`} className="ep-relier-cell">
                        {enCahier && <span className="ep-relier-marque">{String.fromCharCode(65 + i)}.</span>}
                        <HtmlWithKatex html={t} />
                    </div>
                ))}
            </div>
        </div>
    );
};

// ─── Ordonner ──────────────────────────────────────────────────────────

const OrdonnerBody = ({ items, enCahier }) => {
    if (!items?.length) return null;
    return (
        <>
            <div className="ep-ordonner-items">
                {items.map((item, i) => (
                    <span key={i} className="ep-ordonner-chip">
                        <HtmlWithKatex as="span" html={item} />
                    </span>
                ))}
            </div>
            {/* Sur la feuille : une ligne pour recopier l'ordre */}
            {!enCahier && (
                <div className="ep-answer-lines ep-answer-lines-short">
                    <div className="ep-answer-line" />
                </div>
            )}
        </>
    );
};

// ─── Classer ───────────────────────────────────────────────────────────

const ClasserBody = ({ categories, seed, enCahier }) => {
    if (!categories?.length) return null;
    // Items mélangés, affichés sur une ligne séparés par `•`.
    const allItems = categories.flatMap((c) => c.items || []);
    const shuffled = seededShuffle(allItems, seed);
    return (
        <>
            <div className="ep-classer-items">
                {shuffled.map((item, i) => (
                    <span key={i} className="ep-classer-chip">
                        <HtmlWithKatex as="span" html={item} />
                    </span>
                ))}
            </div>
            {enCahier ? (
                // Cahier : pas de tableau vide à remplir — juste le rappel des
                // catégories (gain de place, l'élève trace son tableau lui-même)
                <p className="ep-classer-cats">
                    <strong>Catégories :</strong>{' '}
                    {categories.map((cat) => cat.name).join('  •  ')}
                </p>
            ) : (
                <table className="ep-classer-table">
                    <thead>
                        <tr>
                            {categories.map((cat, i) => (
                                <th key={i}>{cat.name}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {categories.map((_, i) => (
                                <td key={i} className="ep-classer-cell" />
                            ))}
                        </tr>
                    </tbody>
                </table>
            )}
        </>
    );
};

// ─── Texte à trous ─────────────────────────────────────────────────────

const TEXTE_TROU_RE = /\{\{([^}]+)\}\}/g;

/**
 * Pré-traitement des triples accolades du LaTeX. Sans ça, `\dfrac{{{4}}}`
 * casse parce que la regex `{{...}}` mange l'accolade ouvrante de `\dfrac`
 * (greedy sur `[^}]+`). Identique au renderer élève
 * (`TexteTrouRenderer.jsx` ligne 8).
 *   `{{{` → `{ {{`   ;   `}}}` → `}} }`
 */
const preprocessBraces = (s) =>
    (s || '').replace(/\{\{\{/g, '{ {{').replace(/\}\}\}/g, '}} }');

/**
 * Un trou sur la feuille : des points de suspension, le signe des manuels
 * (« 37/4 = … + …/4 »), en texte comme en formule (2026-09-09, décision
 * prof). L'élève RECOPIE sur son cahier (consigne « Recopie sur ton
 * cahier… ») : le trait à souligner de 80 px disait « écris ici » et le
 * carré `\boxed` alourdissait les fractions, deux signes pour une chose.
 */
const SLOT_HTML = '<span class="ep-tt-trou">…</span>';
const SLOT_TEX = '\\ldots';
/**
 * Étais de largeur nulle (`\rule[-0.55em]{0pt}{0.55em}`, `\rule{0pt}{1.3em}`)
 * posés par les gens autour des cases de fraction pour leur hauteur À
 * L'ÉCRAN : sans case, ils ne font qu'éloigner le « … » de la barre de
 * fraction (constat prof 2026-09-09). Retirés au print, seulement ceux de
 * largeur 0pt (une barre visible reste une barre).
 */
const ETAI_RE = /\\rule(\[[^\]]*\])?\{0pt\}\{[^}]*\}/g;
export const trousEnPointilles = (ligne) => ligne.replace(/\{\{[^}]+\}\}/g, () => SLOT_TEX).replace(ETAI_RE, '');

/**
 * Détecte si la phrase a du KaTeX qui englobe un `{{trou}}`.
 * Cas typique : `$\text{...} 2 \times {{6}} = 6.$` — splitter casse le `$...$`.
 *
 * Testé formule par formule, ligne par ligne. L'ancienne regex globale
 * (`\$[^$]*\{\{…\}\}[^$]*\$`) traversait les sauts de ligne : sur
 * « … {{0,9}} | $\frac{9}{10}$ …\n… {{0,7}} | $\frac{7}{10}$ … » elle
 * reliait le `$` fermant d'une ligne au `$` ouvrant de la suivante, trou
 * entre les deux, et un `\boxed` s'imprimait en clair dans le texte
 * (gen_600, constat utilisateur 2026-09-06).
 */
export const katexEnglobeUnTrou = (phrase) =>
    (phrase || '').split('\n').some((ligne) =>
        (ligne.match(/\$[^$\n]*\$/g) || []).some((formule) => /\{\{[^}]+\}\}/.test(formule)));

const TexteTrouBody = ({ phrase, mode, distracteurs, seed }) => {
    if (!phrase) return null;
    // Préprocessing : sépare les `{{{` LaTeX des `{{` trous pour que les regex
    // suivantes ne mangent pas les accolades de `\dfrac{...}` etc.
    const safe = preprocessBraces(phrase);
    const answers = [...safe.matchAll(TEXTE_TROU_RE)].map((m) => m[1].trim());
    const banque = mode === 'drag'
        ? seededShuffle([...answers, ...(distracteurs || [])], seed)
        : null;

    // Cas KaTeX englobant : on rend la phrase entière en remplaçant les trous
    // par `\ldots` (même signe que `.ep-tt-trou`).
    const hasKatexWrappingTrou = katexEnglobeUnTrou(safe);

    // Feuille comme cahier : trous laissés en BLANC — en cahier l'élève
    // RECOPIE la phrase en la complétant (consigne « Recopie sur ton
    // cahier… ») ; les numéros (1) (2) sont abandonnés (décision 2026-08-28).

    return (
        <>
            <div className="ep-tt-phrase">
                {hasKatexWrappingTrou ? (
                    // Une LIGNE de la phrase = un bloc, comme à l'écran : les
                    // gens multi-égalités (\n entre les $...$, ex. gen_1005)
                    // s'imprimaient côte à côte, illisibles (constat
                    // utilisateur 2026-08-19).
                    safe.split('\n').map((ligne, li) => {
                        const t = ligne.trim();
                        if (!t) return null;
                        return (
                            <div key={li} className="ep-tt-ligne">
                                <HtmlWithKatex
                                    as="span"
                                    html={trousEnPointilles(t)}
                                />
                            </div>
                        );
                    })
                ) : (
                    // Mode classique : une LIGNE imprimée par \n du générateur
                    // (dans du HTML le \n n'est qu'un espace : 49 gens
                    // s'imprimaient collés, constat utilisateur 2026-09-02),
                    // et une phrase par ligne quand la ligne porte plusieurs
                    // égalités à trous (lignesTrous). Les cases passent par un
                    // jeton SLOT pour rester DANS le bloc insécable de leur
                    // égalité : jamais « 6 × 1,30 = » sur une ligne et la
                    // case sur la suivante (cf. helpers/expressions.js).
                    // Une CHAÎNE d'égalités (« 1 m³ = 1 m × 1 m × 1 m = … dm ×
                    // … dm × … dm = … dm³ ») : tête + membres « = … »
                    // insécables, repli seulement au « = » (2026-09-09).
                    // TABLEAU : toutes les lignes en « libellé : contenu » (« Étape 1 : 5 »,
                    // « Étape 6 : ⬚ ») → deux colonnes, contenus alignés (même règle qu'à
                    // l'écran, cf. enTableau ; « Étape 5 » et « Étape 12 » n'ont pas la
                    // même largeur et les cases se décalaient).
                    ((toutes) => {
                        const htmlTrous = (s) => renderHtmlWithKatex(s.replace(/\{\{[^}]+\}\}/g, SLOT)).split(SLOT).join(SLOT_HTML);
                        const paires = toutes.map((l) => {
                            const i = l.indexOf(' : ');
                            return i > 0 && l.slice(i + 3).trim() ? [l.slice(0, i), l.slice(i + 3)] : null;
                        });
                        if (toutes.length >= 2 && paires.every(Boolean)) {
                            return (
                                <span className="ep-tt-tableau">
                                    {paires.map(([g, d], li) => (
                                        <span key={li} className="ep-tt-rangee">
                                            <span dangerouslySetInnerHTML={{ __html: htmlTrous(`${g} :`) }} />
                                            <span dangerouslySetInnerHTML={{ __html: htmlTrous(d) }} />
                                        </span>
                                    ))}
                                </span>
                            );
                        }
                        return toutes.map((ligne, li) => {
                        const marquee = ligne.replace(/\{\{[^}]+\}\}/g, SLOT);
                        const chaine = membresChaine(marquee);
                        if (chaine) {
                            return (
                                <span key={li} className="ep-tt-ligne-texte ep-tt-chaine">
                                    <span dangerouslySetInnerHTML={{ __html: htmlTrous(chaine.tete) }} />
                                    <span className="ep-tt-chaine-membres">
                                        {chaine.membres.map((m, k) => (
                                            <span key={k} className="ep-tt-membre" dangerouslySetInnerHTML={{ __html: htmlTrous(m) }} />
                                        ))}
                                    </span>
                                </span>
                            );
                        }
                        return (
                            <span
                                key={li}
                                className="ep-tt-ligne-texte"
                                dangerouslySetInnerHTML={{ __html: htmlTrous(marquee) }}
                            />
                        );
                        });
                    })(lignesTrous(safe))
                )}
            </div>
            {banque && banque.length > 0 && (
                <div className="ep-tt-banque">
                    {banque.map((word, i) => (
                        <span key={i} className="ep-tt-banque-word">
                            <HtmlWithKatex as="span" html={word} />
                        </span>
                    ))}
                </div>
            )}
        </>
    );
};

// ─── Placer des points ─────────────────────────────────────────────────

const PlacerPointsBody = ({ numberline, pointsToPlace }) => {
    if (!numberline) return null;
    const labels = Array.isArray(pointsToPlace) ? pointsToPlace.join(', ') : '';
    return (
        <>
            {labels && (
                <p className="ep-placer-labels">
                    <strong>Points à placer :</strong> {labels}
                </p>
            )}
            <div className="ep-placer-line">
                <PrintNumberLine {...numberline} />
            </div>
        </>
    );
};

// ─── Placer des points dans un repère (2D) ─────────────────────────────

const Repere2DBody = ({ repere, pointsToPlace, dejaPlaces, decor }) => {
    if (!repere) return null;
    const labels = Array.isArray(pointsToPlace) ? pointsToPlace.join(', ') : '';
    return (
        <>
            {labels && (
                <p className="ep-placer-labels">
                    <strong>Points à placer :</strong> {labels}
                </p>
            )}
            <div className="ep-placer-line">
                <PrintRepere2D repere={repere} dejaPlaces={dejaPlaces || []} decor={decor || null} />
            </div>
        </>
    );
};

// ─── Programme de construction ─────────────────────────────────────────

const ProgrammeConstructionBody = ({ contenu }) => {
    if (!contenu) return null;
    const { viewBox, pxPerCm, points, preShapes, tasks } = contenu;
    const unite = contenu.unite || 'cm';
    const list = Array.isArray(tasks) ? tasks : [];
    return (
        <>
            {/* Unité de la figure : au print la taille physique dépend de la mise
                en page (max-width 100 %) — le carreau EST l'unité, jamais le cm
                réel de la règle. C'est cette mention qui légitime le geste :
                l'élève règle son compas avec la grille. */}
            <p className="ep-pc-unite">Sur la figure : 1 carreau = 1 {unite}.</p>
            {list.length > 0 && (
                <ol className="ep-pc-tasks">
                    {list.map((task, i) => (
                        <li key={task.id ?? i} className="ep-pc-task">
                            <span
                                className="ep-pc-chip"
                                style={{ background: task.color || '#334155' }}
                            >
                                {task.id || String.fromCharCode(97 + i)}
                            </span>
                            {/* Photocopie N&B : la couleur de la pastille devient un
                                gris indistinct — le nom en toutes lettres garde la
                                consigne « trace en bleu… » exécutable. */}
                            {nomCouleur(task.color) ? (
                                <span className="ep-pc-chip-nom">({nomCouleur(task.color)})</span>
                            ) : null}
                            <HtmlWithKatex
                                as="span"
                                className="ep-pc-consigne"
                                html={task.consigne || ''}
                            />
                        </li>
                    ))}
                </ol>
            )}
            <PrintConstructionFigure
                viewBox={viewBox}
                pxPerCm={pxPerCm}
                points={points}
                preShapes={preShapes}
            />
        </>
    );
};

// ─── Placer les aiguilles (horloge) ────────────────────────────────────

/** Cadran VIERGE à compléter au crayon (l'heure est dans l'énoncé). */
const PrintHorlogeVierge = () => {
    const elems = [];
    for (let i = 0; i < 60; i++) {
        const maj = i % 5 === 0;
        const rad = (i * 6 * Math.PI) / 180;
        const r1 = maj ? 96 : 101;
        elems.push(
            <line
                key={`g${i}`}
                x1={120 + r1 * Math.sin(rad)} y1={120 - r1 * Math.cos(rad)}
                x2={120 + 106 * Math.sin(rad)} y2={120 - 106 * Math.cos(rad)}
                stroke={maj ? '#334155' : '#94a3b8'} strokeWidth={maj ? 2.5 : 1}
            />,
        );
    }
    for (let i = 1; i <= 12; i++) {
        const rad = (i * 30 * Math.PI) / 180;
        elems.push(
            <text
                key={`n${i}`} x={120 + 80 * Math.sin(rad)} y={120 - 80 * Math.cos(rad) + 6}
                fontSize={17} fontWeight={700} fill="#111827"
                textAnchor="middle" fontFamily="system-ui, sans-serif"
            >
                {i}
            </text>,
        );
    }
    return (
        <svg viewBox="0 0 240 240" style={{ width: 180, height: 180, display: 'block', margin: '4px auto' }}>
            <circle cx={120} cy={120} r={112} fill="#ffffff" stroke="#111827" strokeWidth={3} />
            {elems}
            <circle cx={120} cy={120} r={4} fill="#111827" />
        </svg>
    );
};

const PlacerAiguillesBody = ({ contenu }) => (
    <>
        {contenu?.avecTrotteuse ? (
            <p className="ep-pc-unite">N'oublie pas la trotteuse (les secondes).</p>
        ) : null}
        <PrintHorlogeVierge />
    </>
);

// ─── Dispatcher ────────────────────────────────────────────────────────


/**
 * Corps d'un exercice d'association (dispatch par `contenu.subType`), sans
 * header ni consigne ni énoncé — mention « feuille » incluse (elle dépend du
 * mode de l'item, pas du bloc). Partagé par le single et les blocs à figure
 * partagée (`PrintFigureGroup`).
 */
export const AssociationBody = ({ payload, saisieMode = 'sur_la_feuille' }) => {
    const sub = payload?.contenu?.subType;
    const mode = modeEffectif(payload, saisieMode);
    const enCahier = mode === 'cahier';
    const seed = seedFromPayload(payload);

    return (
        <>
            {/* Formats où répondre sur la feuille EST l'exercice : mention
                discrète quand le document est globalement « sur le cahier » */}
            {mode === 'feuille_forcee' && (
                <p className="ep-note-feuille">Réponds directement sur la feuille.</p>
            )}
            {sub === 'relier' && (
                <RelierBody pairs={payload.contenu.pairs} ciblesLeurres={payload.contenu.cibles_leurres} seed={seed} enCahier={enCahier} />
            )}
            {sub === 'ordonner' && (
                <OrdonnerBody items={payload.contenu.items} enCahier={enCahier} />
            )}
            {sub === 'classer' && (
                <ClasserBody categories={payload.contenu.categories} seed={seed} enCahier={enCahier} />
            )}
            {sub === 'texte_trou' && (
                <TexteTrouBody
                    phrase={payload.contenu.phrase}
                    mode={payload.contenu.mode}
                    distracteurs={payload.contenu.distracteurs}
                    seed={seed}
                />
            )}
            {sub === 'placer_points' && (
                <PlacerPointsBody
                    numberline={payload.contenu.numberline}
                    pointsToPlace={payload.contenu.pointsToPlace}
                />
            )}
            {sub === 'placer_points_2d' && (
                <Repere2DBody
                    repere={payload.contenu.repere}
                    pointsToPlace={payload.contenu.pointsToPlace}
                    dejaPlaces={payload.contenu.dejaPlaces}
                    decor={payload.contenu.decor}
                />
            )}
            {sub === 'placer_aiguilles' && (
                <PlacerAiguillesBody contenu={payload.contenu} />
            )}
            {sub === 'programme_construction' && (
                <ProgrammeConstructionBody contenu={payload.contenu} />
            )}
        </>
    );
};

const PrintAssociation = ({ payload, num, saisieMode = 'sur_la_feuille' }) => {
    const consigne = consignePour(payload, saisieMode);

    return (
        <div className="ep-exo ep-assoc">
            <span className="ep-exo-num">{num}</span>
            {consigne && <p className="ep-exo-consigne">{consigne}</p>}
            {payload?.texte && (
                <PrintEnonce
                    texte={payload.texte}
                    elements={payload?.elements_additionnels?.elements}
                />
            )}
            <AssociationBody payload={payload} saisieMode={saisieMode} />
        </div>
    );
};

export default PrintAssociation;
